import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Layers, Users, Wand2, Trash2 } from "lucide-react";
import { useCompany } from "../../context/CompanyContext";
import {
  ensurePayrollPhase4Bootstrap,
  getPayrollJobLevels,
  createPayrollJobLevel,
  getPayrollJobGrades,
  createPayrollJobGrade,
  getPayrollGradeFramework,
  getPayrollGradeCompensationProfile,
  upsertPayrollGradeCompensationProfile,
  getPayrollGradeComponentDefaults,
  upsertPayrollGradeComponentDefault,
  deletePayrollGradeComponentDefault,
  getPayrollGradeBenefitDefaults,
  upsertPayrollGradeBenefitDefault,
  deletePayrollGradeBenefitDefault,
  getPayComponents,
  getBenefitPlans,
  getEmployees,
  applyGradeTemplateToEmployee,
} from "../payrollService";

const initialLevelForm = {
  level_code: "",
  level_name: "",
  level_rank: "",
  description: "",
  active: true,
};

const initialGradeForm = {
  level_id: "",
  grade_code: "",
  grade_name: "",
  grade_rank: "",
  description: "",
  active: true,
};

const initialProfileForm = {
  salary_basis: "monthly",
  base_salary: "",
  hourly_rate: "",
  currency_code: "NGN",
  auto_apply_statutory: true,
  tax_exempt: false,
  pension_exempt: false,
  nhf_exempt: false,
  nhis_exempt: false,
  leave_policy_group: "",
  notes: "",
};

const initialApplyForm = {
  employee_id: "",
  grade_id: "",
  mode: "replace",
};

const initialComponentDefaultForm = {
  component_id: "",
  amount: "",
  percentage: "",
  active: true,
  notes: "",
};

const initialBenefitDefaultForm = {
  benefit_plan_id: "",
  employee_cost_override: "",
  employer_cost_override: "",
  active: true,
  notes: "",
};

function pickOne(value) {
  if (!value) return null;
  return Array.isArray(value) ? value[0] || null : value;
}

export default function PayrollStructures() {
  const { companyId } = useCompany();

  const [busy, setBusy] = useState(true);
  const [msg, setMsg] = useState("");

  const [levels, setLevels] = useState([]);
  const [grades, setGrades] = useState([]);
  const [framework, setFramework] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [components, setComponents] = useState([]);
  const [benefitPlans, setBenefitPlans] = useState([]);

  const [selectedLevelId, setSelectedLevelId] = useState("");
  const [selectedGradeId, setSelectedGradeId] = useState("");
  const [, setProfile] = useState(null);

  const [gradeComponentDefaults, setGradeComponentDefaults] = useState([]);
  const [gradeBenefitDefaults, setGradeBenefitDefaults] = useState([]);

  const [showLevelModal, setShowLevelModal] = useState(false);
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);

  const [levelForm, setLevelForm] = useState(initialLevelForm);
  const [gradeForm, setGradeForm] = useState(initialGradeForm);
  const [profileForm, setProfileForm] = useState(initialProfileForm);
  const [applyForm, setApplyForm] = useState(initialApplyForm);
  const [componentDefaultForm, setComponentDefaultForm] = useState(
    initialComponentDefaultForm
  );
  const [benefitDefaultForm, setBenefitDefaultForm] = useState(
    initialBenefitDefaultForm
  );

  const [savingProfile, setSavingProfile] = useState(false);
  const [applyingGrade, setApplyingGrade] = useState(false);
  const [savingComponentDefault, setSavingComponentDefault] = useState(false);
  const [savingBenefitDefault, setSavingBenefitDefault] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;

    setBusy(true);
    setMsg("");

    try {
      await ensurePayrollPhase4Bootstrap(companyId);

      const [
        levelsData,
        gradesData,
        frameworkData,
        employeesData,
        componentsData,
        benefitsData,
      ] = await Promise.all([
        getPayrollJobLevels(companyId),
        getPayrollJobGrades(companyId),
        getPayrollGradeFramework(companyId),
        getEmployees(companyId),
        getPayComponents(companyId),
        getBenefitPlans(companyId),
      ]);

      setLevels(levelsData || []);
      setGrades(gradesData || []);
      setFramework(frameworkData || []);
      setEmployees(employeesData || []);
      setComponents(componentsData || []);
      setBenefitPlans(benefitsData || []);

      if (!selectedLevelId && levelsData?.length) {
        setSelectedLevelId(levelsData[0].id);
      }
    } catch (err) {
      setMsg(err.message || "Failed to load payroll structures");
    } finally {
      setBusy(false);
    }
  }, [companyId, selectedLevelId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    async function loadGradeDetails() {
      if (!companyId || !selectedGradeId) {
        setProfile(null);
        setProfileForm(initialProfileForm);
        setGradeComponentDefaults([]);
        setGradeBenefitDefaults([]);
        return;
      }

      try {
        const [profileData, componentDefaultsData, benefitDefaultsData] =
          await Promise.all([
            getPayrollGradeCompensationProfile(companyId, selectedGradeId),
            getPayrollGradeComponentDefaults(companyId, selectedGradeId),
            getPayrollGradeBenefitDefaults(companyId, selectedGradeId),
          ]);

        setProfile(profileData || null);
        setGradeComponentDefaults(componentDefaultsData || []);
        setGradeBenefitDefaults(benefitDefaultsData || []);

        setProfileForm({
          salary_basis: profileData?.salary_basis || "monthly",
          base_salary: profileData?.base_salary ?? "",
          hourly_rate: profileData?.hourly_rate ?? "",
          currency_code: profileData?.currency_code || "NGN",
          auto_apply_statutory: profileData?.auto_apply_statutory ?? true,
          tax_exempt: profileData?.tax_exempt ?? false,
          pension_exempt: profileData?.pension_exempt ?? false,
          nhf_exempt: profileData?.nhf_exempt ?? false,
          nhis_exempt: profileData?.nhis_exempt ?? false,
          leave_policy_group: profileData?.leave_policy_group || "",
          notes: profileData?.notes || "",
        });
      } catch (err) {
        setMsg(err.message || "Failed to load grade details");
      }
    }

    loadGradeDetails();
  }, [companyId, selectedGradeId]);

  const filteredGrades = useMemo(() => {
    if (!selectedLevelId) return grades;
    return grades.filter((x) => x.level_id === selectedLevelId);
  }, [grades, selectedLevelId]);

  const selectedLevel = useMemo(
    () => levels.find((x) => x.id === selectedLevelId) || null,
    [levels, selectedLevelId]
  );

  const selectedGrade = useMemo(
    () => grades.find((x) => x.id === selectedGradeId) || null,
    [grades, selectedGradeId]
  );

  const selectableComponents = useMemo(() => {
    return (components || []).filter((row) => {
      const code = String(row.component_code || "").toUpperCase();
      return code !== "BASIC" && code !== "BASE";
    });
  }, [components]);

  async function handleCreateLevel(e) {
    e.preventDefault();

    try {
      setMsg("");

      await createPayrollJobLevel({
        company_id: companyId,
        level_code: levelForm.level_code.trim().toUpperCase(),
        level_name: levelForm.level_name.trim(),
        level_rank: Number(levelForm.level_rank || 1),
        description: levelForm.description || null,
        active: !!levelForm.active,
      });

      setLevelForm(initialLevelForm);
      setShowLevelModal(false);
      await load();
      setMsg("Level created successfully.");
    } catch (err) {
      setMsg(err.message || "Failed to create level");
    }
  }

  async function handleCreateGrade(e) {
    e.preventDefault();

    try {
      setMsg("");

      await createPayrollJobGrade({
        company_id: companyId,
        level_id: gradeForm.level_id,
        grade_code: gradeForm.grade_code.trim().toUpperCase(),
        grade_name: gradeForm.grade_name.trim(),
        grade_rank: Number(gradeForm.grade_rank || 1),
        description: gradeForm.description || null,
        active: !!gradeForm.active,
      });

      setGradeForm(initialGradeForm);
      setShowGradeModal(false);
      await load();
      setMsg("Grade created successfully.");
    } catch (err) {
      setMsg(err.message || "Failed to create grade");
    }
  }

  async function handleSaveProfile(e) {
    e.preventDefault();
    if (!selectedGradeId) {
      setMsg("Please select a grade first.");
      return;
    }

    try {
      setSavingProfile(true);
      setMsg("");

      await upsertPayrollGradeCompensationProfile({
        company_id: companyId,
        grade_id: selectedGradeId,
        salary_basis: profileForm.salary_basis || "monthly",
        base_salary: Number(profileForm.base_salary || 0),
        hourly_rate:
          profileForm.hourly_rate === ""
            ? null
            : Number(profileForm.hourly_rate),
        currency_code: profileForm.currency_code || "NGN",
        auto_apply_statutory: !!profileForm.auto_apply_statutory,
        tax_exempt: !!profileForm.tax_exempt,
        pension_exempt: !!profileForm.pension_exempt,
        nhf_exempt: !!profileForm.nhf_exempt,
        nhis_exempt: !!profileForm.nhis_exempt,
        leave_policy_group: profileForm.leave_policy_group || null,
        notes: profileForm.notes || null,
      });

      const data = await getPayrollGradeCompensationProfile(
        companyId,
        selectedGradeId
      );
      setProfile(data || null);
      await load();
      setMsg("Compensation profile saved successfully.");
    } catch (err) {
      setMsg(err.message || "Failed to save compensation profile");
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleSaveComponentDefault(e) {
    e.preventDefault();
    if (!selectedGradeId) {
      setMsg("Please select a grade first.");
      return;
    }

    try {
      setSavingComponentDefault(true);
      setMsg("");

      await upsertPayrollGradeComponentDefault({
        company_id: companyId,
        grade_id: selectedGradeId,
        component_id: componentDefaultForm.component_id,
        amount:
          componentDefaultForm.amount === ""
            ? null
            : Number(componentDefaultForm.amount),
        percentage:
          componentDefaultForm.percentage === ""
            ? null
            : Number(componentDefaultForm.percentage),
        active: !!componentDefaultForm.active,
        notes: componentDefaultForm.notes || null,
      });

      const data = await getPayrollGradeComponentDefaults(
        companyId,
        selectedGradeId
      );
      setGradeComponentDefaults(data || []);
      setComponentDefaultForm(initialComponentDefaultForm);
      setMsg("Grade component default saved successfully.");
    } catch (err) {
      setMsg(err.message || "Failed to save grade component default");
    } finally {
      setSavingComponentDefault(false);
    }
  }

  async function handleDeleteComponentDefault(id) {
    try {
      setMsg("");
      await deletePayrollGradeComponentDefault(id);

      const data = await getPayrollGradeComponentDefaults(
        companyId,
        selectedGradeId
      );
      setGradeComponentDefaults(data || []);
      setMsg("Grade component default removed.");
    } catch (err) {
      setMsg(err.message || "Failed to remove grade component default");
    }
  }

  async function handleSaveBenefitDefault(e) {
    e.preventDefault();
    if (!selectedGradeId) {
      setMsg("Please select a grade first.");
      return;
    }

    try {
      setSavingBenefitDefault(true);
      setMsg("");

      await upsertPayrollGradeBenefitDefault({
        company_id: companyId,
        grade_id: selectedGradeId,
        benefit_plan_id: benefitDefaultForm.benefit_plan_id,
        employee_cost_override:
          benefitDefaultForm.employee_cost_override === ""
            ? null
            : Number(benefitDefaultForm.employee_cost_override),
        employer_cost_override:
          benefitDefaultForm.employer_cost_override === ""
            ? null
            : Number(benefitDefaultForm.employer_cost_override),
        active: !!benefitDefaultForm.active,
        notes: benefitDefaultForm.notes || null,
      });

      const data = await getPayrollGradeBenefitDefaults(
        companyId,
        selectedGradeId
      );
      setGradeBenefitDefaults(data || []);
      setBenefitDefaultForm(initialBenefitDefaultForm);
      setMsg("Grade benefit default saved successfully.");
    } catch (err) {
      setMsg(err.message || "Failed to save grade benefit default");
    } finally {
      setSavingBenefitDefault(false);
    }
  }

  async function handleDeleteBenefitDefault(id) {
    try {
      setMsg("");
      await deletePayrollGradeBenefitDefault(id);

      const data = await getPayrollGradeBenefitDefaults(
        companyId,
        selectedGradeId
      );
      setGradeBenefitDefaults(data || []);
      setMsg("Grade benefit default removed.");
    } catch (err) {
      setMsg(err.message || "Failed to remove grade benefit default");
    }
  }

  async function handleApplyGrade(e) {
    e.preventDefault();

    try {
      setApplyingGrade(true);
      setMsg("");

      await applyGradeTemplateToEmployee({
        companyId,
        employeeId: applyForm.employee_id,
        gradeId: applyForm.grade_id,
        mode: applyForm.mode,
      });

      setShowApplyModal(false);
      setApplyForm(initialApplyForm);
      await load();
      setMsg("Grade template applied successfully.");
    } catch (err) {
      setMsg(err.message || "Failed to apply grade template");
    } finally {
      setApplyingGrade(false);
    }
  }

  return (
    <div className="payroll-page">
      {msg ? <div className="payroll-alert info">{msg}</div> : null}

      <div className="payroll-grid payroll-grid-4">
        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Levels</p>
          <h3>{levels.length}</h3>
          <span>Career families</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Grades</p>
          <h3>{grades.length}</h3>
          <span>Assignable employee grades</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Profiles</p>
          <h3>{framework.filter((x) => x.compensation_profile_id).length}</h3>
          <span>Grade compensation profiles</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Employees Linked</p>
          <h3>{employees.filter((x) => x.job_grade_id).length}</h3>
          <span>Employees with level/grade</span>
        </div>
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>Compensation Framework Builder</h3>
            <p>
              Create job levels and grades, define defaults, and apply them to
              employees.
            </p>
          </div>

          <div className="table-actions">
            <button
              type="button"
              className="ghost-btn"
              onClick={() => setShowLevelModal(true)}
            >
              <Plus size={16} />
              Add Level
            </button>

            <button
              type="button"
              className="ghost-btn"
              onClick={() => {
                setGradeForm((p) => ({
                  ...p,
                  level_id: selectedLevelId || "",
                }));
                setShowGradeModal(true);
              }}
            >
              <Plus size={16} />
              Add Grade
            </button>

            <button
              type="button"
              className="primary-btn"
              onClick={() => setShowApplyModal(true)}
            >
              <Wand2 size={16} />
              Apply Grade to Employee
            </button>
          </div>
        </div>

        <div className="payroll-grid payroll-grid-3">
          <div className="payroll-card">
            <div className="payroll-card-header">
              <div>
                <h3>Levels</h3>
                <p>Select a level to view its grades.</p>
              </div>
            </div>

            <div className="structures-list">
              {levels.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  className={`structure-list-item ${
                    selectedLevelId === row.id ? "active" : ""
                  }`}
                  onClick={() => {
                    setSelectedLevelId(row.id);
                    setSelectedGradeId("");
                  }}
                >
                  <div>
                    <strong>{row.level_name}</strong>
                    <span>{row.level_code}</span>
                  </div>
                  <span className={`pill ${row.active ? "approved" : "draft"}`}>
                    #{row.level_rank}
                  </span>
                </button>
              ))}

              {!levels.length && !busy ? (
                <div className="structure-empty">No levels found.</div>
              ) : null}
            </div>
          </div>

          <div className="payroll-card">
            <div className="payroll-card-header">
              <div>
                <h3>Grades</h3>
                <p>
                  {selectedLevel
                    ? `Grades under ${selectedLevel.level_name}`
                    : "Select a level first"}
                </p>
              </div>
            </div>

            <div className="structures-list">
              {filteredGrades.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  className={`structure-list-item ${
                    selectedGradeId === row.id ? "active" : ""
                  }`}
                  onClick={() => setSelectedGradeId(row.id)}
                >
                  <div>
                    <strong>{row.grade_name}</strong>
                    <span>{row.grade_code}</span>
                  </div>
                  <span className={`pill ${row.active ? "approved" : "draft"}`}>
                    #{row.grade_rank}
                  </span>
                </button>
              ))}

              {!filteredGrades.length && !busy ? (
                <div className="structure-empty">
                  No grades found for this level.
                </div>
              ) : null}
            </div>
          </div>

          <div className="payroll-card">
            <div className="payroll-card-header">
              <div>
                <h3>Selected Grade</h3>
                <p>Quick overview of the selected framework item.</p>
              </div>
            </div>

            {selectedGrade ? (
              <div className="structure-overview">
                <div className="structure-overview-row">
                  <span>Level</span>
                  <strong>{selectedLevel?.level_name || "-"}</strong>
                </div>
                <div className="structure-overview-row">
                  <span>Grade</span>
                  <strong>{selectedGrade.grade_name}</strong>
                </div>
                <div className="structure-overview-row">
                  <span>Code</span>
                  <strong>{selectedGrade.grade_code}</strong>
                </div>
                <div className="structure-overview-row">
                  <span>Status</span>
                  <strong>
                    {selectedGrade.active ? "Active" : "Inactive"}
                  </strong>
                </div>
                <div className="structure-overview-row">
                  <span>Description</span>
                  <strong>{selectedGrade.description || "-"}</strong>
                </div>
              </div>
            ) : (
              <div className="structure-empty">Select a grade to continue.</div>
            )}
          </div>
        </div>
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>Grade Compensation Profile</h3>
            <p>
              Set base salary, statutory behavior, and other defaults for the
              selected grade.
            </p>
          </div>
        </div>

        {selectedGrade ? (
          <form className="payroll-form" onSubmit={handleSaveProfile}>
            <div className="payroll-grid payroll-grid-3">
              <label>
                Salary Basis
                <select
                  value={profileForm.salary_basis}
                  onChange={(e) =>
                    setProfileForm((p) => ({
                      ...p,
                      salary_basis: e.target.value,
                    }))
                  }
                >
                  <option value="monthly">Monthly</option>
                  <option value="hourly">Hourly</option>
                </select>
              </label>

              <label>
                Base Salary
                <input
                  type="number"
                  step="0.01"
                  value={profileForm.base_salary}
                  onChange={(e) =>
                    setProfileForm((p) => ({
                      ...p,
                      base_salary: e.target.value,
                    }))
                  }
                />
              </label>

              <label>
                Hourly Rate
                <input
                  type="number"
                  step="0.01"
                  value={profileForm.hourly_rate}
                  onChange={(e) =>
                    setProfileForm((p) => ({
                      ...p,
                      hourly_rate: e.target.value,
                    }))
                  }
                />
              </label>

              <label>
                Currency Code
                <input
                  value={profileForm.currency_code}
                  onChange={(e) =>
                    setProfileForm((p) => ({
                      ...p,
                      currency_code: e.target.value,
                    }))
                  }
                />
              </label>

              <label>
                Leave Policy Group
                <input
                  value={profileForm.leave_policy_group}
                  onChange={(e) =>
                    setProfileForm((p) => ({
                      ...p,
                      leave_policy_group: e.target.value,
                    }))
                  }
                />
              </label>
            </div>

            <div className="payroll-grid payroll-grid-2">
              <label className="check-inline">
                <input
                  type="checkbox"
                  checked={profileForm.auto_apply_statutory}
                  onChange={(e) =>
                    setProfileForm((p) => ({
                      ...p,
                      auto_apply_statutory: e.target.checked,
                    }))
                  }
                />
                Auto Apply Statutory Rules
              </label>

              <label className="check-inline">
                <input
                  type="checkbox"
                  checked={profileForm.tax_exempt}
                  onChange={(e) =>
                    setProfileForm((p) => ({
                      ...p,
                      tax_exempt: e.target.checked,
                    }))
                  }
                />
                Tax Exempt
              </label>

              <label className="check-inline">
                <input
                  type="checkbox"
                  checked={profileForm.pension_exempt}
                  onChange={(e) =>
                    setProfileForm((p) => ({
                      ...p,
                      pension_exempt: e.target.checked,
                    }))
                  }
                />
                Pension Exempt
              </label>

              <label className="check-inline">
                <input
                  type="checkbox"
                  checked={profileForm.nhf_exempt}
                  onChange={(e) =>
                    setProfileForm((p) => ({
                      ...p,
                      nhf_exempt: e.target.checked,
                    }))
                  }
                />
                NHF Exempt
              </label>

              <label className="check-inline">
                <input
                  type="checkbox"
                  checked={profileForm.nhis_exempt}
                  onChange={(e) =>
                    setProfileForm((p) => ({
                      ...p,
                      nhis_exempt: e.target.checked,
                    }))
                  }
                />
                NHIS Exempt
              </label>
            </div>

            <label>
              Notes
              <textarea
                rows="4"
                value={profileForm.notes}
                onChange={(e) =>
                  setProfileForm((p) => ({ ...p, notes: e.target.value }))
                }
              />
            </label>

            <div className="payroll-form-actions">
              <button
                type="submit"
                className="primary-btn"
                disabled={savingProfile}
              >
                <Layers size={16} />
                {savingProfile ? "Saving..." : "Save Compensation Profile"}
              </button>
            </div>
          </form>
        ) : (
          <div className="structure-empty">
            Select a grade to configure its profile.
          </div>
        )}
      </div>

      <div className="payroll-grid payroll-grid-2">
        <div className="payroll-card">
          <div className="payroll-card-header">
            <div className="payroll-alert info">
              Basic salary is already taken from the employee base salary field,
              so BASIC/BASE salary components are excluded here to avoid double
              counting.
            </div>
            <div>
              <h3>Grade Component Defaults</h3>
              <p>
                Add allowances, deductions, and employer contributions that
                should apply automatically to this grade.
              </p>
            </div>
          </div>

          {selectedGrade ? (
            <>
              <form
                className="payroll-form"
                onSubmit={handleSaveComponentDefault}
              >
                <div className="payroll-grid payroll-grid-2">
                  <label>
                    Pay Component
                    <select
                      value={componentDefaultForm.component_id}
                      onChange={(e) =>
                        setComponentDefaultForm((p) => ({
                          ...p,
                          component_id: e.target.value,
                        }))
                      }
                      required
                    >
                      <option value="">Select component</option>
                      {selectableComponents.map((row) => (
                        <option key={row.id} value={row.id}>
                          {row.component_name} ({row.component_type})
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    Fixed Amount
                    <input
                      type="number"
                      step="0.01"
                      value={componentDefaultForm.amount}
                      onChange={(e) =>
                        setComponentDefaultForm((p) => ({
                          ...p,
                          amount: e.target.value,
                        }))
                      }
                    />
                  </label>

                  <label>
                    Percentage
                    <input
                      type="number"
                      step="0.0001"
                      value={componentDefaultForm.percentage}
                      onChange={(e) =>
                        setComponentDefaultForm((p) => ({
                          ...p,
                          percentage: e.target.value,
                        }))
                      }
                    />
                  </label>
                </div>

                <label>
                  Notes
                  <textarea
                    rows="3"
                    value={componentDefaultForm.notes}
                    onChange={(e) =>
                      setComponentDefaultForm((p) => ({
                        ...p,
                        notes: e.target.value,
                      }))
                    }
                  />
                </label>

                <label className="check-inline">
                  <input
                    type="checkbox"
                    checked={componentDefaultForm.active}
                    onChange={(e) =>
                      setComponentDefaultForm((p) => ({
                        ...p,
                        active: e.target.checked,
                      }))
                    }
                  />
                  Active
                </label>

                <div className="payroll-form-actions">
                  <button
                    type="submit"
                    className="ghost-btn"
                    disabled={savingComponentDefault}
                  >
                    <Plus size={16} />
                    {savingComponentDefault
                      ? "Saving..."
                      : "Add Component Default"}
                  </button>
                </div>
              </form>

              <div className="payroll-table-wrap">
                <table className="payroll-table">
                  <thead>
                    <tr>
                      <th>Component</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Percentage</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gradeComponentDefaults.map((row) => {
                      const component = pickOne(row.payroll_pay_components);
                      return (
                        <tr key={row.id}>
                          <td>{component?.component_name || "-"}</td>
                          <td>{component?.component_type || "-"}</td>
                          <td>{Number(row.amount || 0).toLocaleString()}</td>
                          <td>
                            {Number(row.percentage || 0).toLocaleString()}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="icon-btn danger"
                              onClick={() =>
                                handleDeleteComponentDefault(row.id)
                              }
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}

                    {!gradeComponentDefaults.length ? (
                      <tr>
                        <td colSpan="5">
                          No component defaults set for this grade.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="structure-empty">Select a grade first.</div>
          )}
        </div>

        <div className="payroll-card">
          <div className="payroll-card-header">
            <div>
              <h3>Grade Benefit Defaults</h3>
              <p>
                Add benefit plans that should apply automatically when this
                grade is assigned to an employee.
              </p>
            </div>
          </div>

          {selectedGrade ? (
            <>
              <form
                className="payroll-form"
                onSubmit={handleSaveBenefitDefault}
              >
                <div className="payroll-grid payroll-grid-2">
                  <label>
                    Benefit Plan
                    <select
                      value={benefitDefaultForm.benefit_plan_id}
                      onChange={(e) =>
                        setBenefitDefaultForm((p) => ({
                          ...p,
                          benefit_plan_id: e.target.value,
                        }))
                      }
                      required
                    >
                      <option value="">Select benefit</option>
                      {benefitPlans.map((row) => (
                        <option key={row.id} value={row.id}>
                          {row.benefit_name} ({row.benefit_type})
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    Employee Cost Override
                    <input
                      type="number"
                      step="0.01"
                      value={benefitDefaultForm.employee_cost_override}
                      onChange={(e) =>
                        setBenefitDefaultForm((p) => ({
                          ...p,
                          employee_cost_override: e.target.value,
                        }))
                      }
                    />
                  </label>

                  <label>
                    Employer Cost Override
                    <input
                      type="number"
                      step="0.01"
                      value={benefitDefaultForm.employer_cost_override}
                      onChange={(e) =>
                        setBenefitDefaultForm((p) => ({
                          ...p,
                          employer_cost_override: e.target.value,
                        }))
                      }
                    />
                  </label>
                </div>

                <label>
                  Notes
                  <textarea
                    rows="3"
                    value={benefitDefaultForm.notes}
                    onChange={(e) =>
                      setBenefitDefaultForm((p) => ({
                        ...p,
                        notes: e.target.value,
                      }))
                    }
                  />
                </label>

                <label className="check-inline">
                  <input
                    type="checkbox"
                    checked={benefitDefaultForm.active}
                    onChange={(e) =>
                      setBenefitDefaultForm((p) => ({
                        ...p,
                        active: e.target.checked,
                      }))
                    }
                  />
                  Active
                </label>

                <div className="payroll-form-actions">
                  <button
                    type="submit"
                    className="ghost-btn"
                    disabled={savingBenefitDefault}
                  >
                    <Plus size={16} />
                    {savingBenefitDefault ? "Saving..." : "Add Benefit Default"}
                  </button>
                </div>
              </form>

              <div className="payroll-table-wrap">
                <table className="payroll-table">
                  <thead>
                    <tr>
                      <th>Benefit</th>
                      <th>Employee Cost</th>
                      <th>Employer Cost</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gradeBenefitDefaults.map((row) => {
                      const benefit = pickOne(row.payroll_benefit_plans);
                      return (
                        <tr key={row.id}>
                          <td>{benefit?.benefit_name || "-"}</td>
                          <td>
                            {Number(
                              row.employee_cost_override || 0
                            ).toLocaleString()}
                          </td>
                          <td>
                            {Number(
                              row.employer_cost_override || 0
                            ).toLocaleString()}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="icon-btn danger"
                              onClick={() => handleDeleteBenefitDefault(row.id)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}

                    {!gradeBenefitDefaults.length ? (
                      <tr>
                        <td colSpan="4">
                          No benefit defaults set for this grade.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="structure-empty">Select a grade first.</div>
          )}
        </div>
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>Framework Overview</h3>
            <p>All grade profiles in one place.</p>
          </div>
        </div>

        <div className="payroll-table-wrap">
          <table className="payroll-table">
            <thead>
              <tr>
                <th>Level</th>
                <th>Grade</th>
                <th>Base Salary</th>
                <th>Salary Basis</th>
                <th>Currency</th>
                <th>Tax</th>
                <th>Pension</th>
                <th>NHF</th>
                <th>NHIS</th>
              </tr>
            </thead>
            <tbody>
              {framework.map((row) => (
                <tr key={`${row.level_id}-${row.grade_id}`}>
                  <td>{row.level_name}</td>
                  <td>{row.grade_name}</td>
                  <td>{Number(row.base_salary || 0).toLocaleString()}</td>
                  <td>{row.salary_basis || "-"}</td>
                  <td>{row.currency_code || "-"}</td>
                  <td>{row.tax_exempt ? "Exempt" : "Apply"}</td>
                  <td>{row.pension_exempt ? "Exempt" : "Apply"}</td>
                  <td>{row.nhf_exempt ? "Exempt" : "Apply"}</td>
                  <td>{row.nhis_exempt ? "Exempt" : "Apply"}</td>
                </tr>
              ))}

              {!framework.length && !busy ? (
                <tr>
                  <td colSpan="9">No framework records found.</td>
                </tr>
              ) : null}

              {busy ? (
                <tr>
                  <td colSpan="9">Loading...</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {showLevelModal ? (
        <div className="payroll-modal-overlay">
          <div className="payroll-modal">
            <div className="payroll-modal-header">
              <h3>Create Job Level</h3>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setShowLevelModal(false)}
              >
                Close
              </button>
            </div>

            <form className="payroll-form" onSubmit={handleCreateLevel}>
              <div className="payroll-grid payroll-grid-2">
                <label>
                  Level Code
                  <input
                    value={levelForm.level_code}
                    onChange={(e) =>
                      setLevelForm((p) => ({
                        ...p,
                        level_code: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label>
                  Level Name
                  <input
                    value={levelForm.level_name}
                    onChange={(e) =>
                      setLevelForm((p) => ({
                        ...p,
                        level_name: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label>
                  Rank
                  <input
                    type="number"
                    value={levelForm.level_rank}
                    onChange={(e) =>
                      setLevelForm((p) => ({
                        ...p,
                        level_rank: e.target.value,
                      }))
                    }
                    required
                  />
                </label>
              </div>

              <label>
                Description
                <textarea
                  rows="4"
                  value={levelForm.description}
                  onChange={(e) =>
                    setLevelForm((p) => ({ ...p, description: e.target.value }))
                  }
                />
              </label>

              <label className="check-inline">
                <input
                  type="checkbox"
                  checked={levelForm.active}
                  onChange={(e) =>
                    setLevelForm((p) => ({ ...p, active: e.target.checked }))
                  }
                />
                Active
              </label>

              <div className="payroll-form-actions">
                <button type="submit" className="primary-btn">
                  Save Level
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {showGradeModal ? (
        <div className="payroll-modal-overlay">
          <div className="payroll-modal">
            <div className="payroll-modal-header">
              <h3>Create Job Grade</h3>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setShowGradeModal(false)}
              >
                Close
              </button>
            </div>

            <form className="payroll-form" onSubmit={handleCreateGrade}>
              <div className="payroll-grid payroll-grid-2">
                <label>
                  Level
                  <select
                    value={gradeForm.level_id}
                    onChange={(e) =>
                      setGradeForm((p) => ({ ...p, level_id: e.target.value }))
                    }
                    required
                  >
                    <option value="">Select level</option>
                    {levels.map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.level_name}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Grade Code
                  <input
                    value={gradeForm.grade_code}
                    onChange={(e) =>
                      setGradeForm((p) => ({
                        ...p,
                        grade_code: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label>
                  Grade Name
                  <input
                    value={gradeForm.grade_name}
                    onChange={(e) =>
                      setGradeForm((p) => ({
                        ...p,
                        grade_name: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label>
                  Rank
                  <input
                    type="number"
                    value={gradeForm.grade_rank}
                    onChange={(e) =>
                      setGradeForm((p) => ({
                        ...p,
                        grade_rank: e.target.value,
                      }))
                    }
                    required
                  />
                </label>
              </div>

              <label>
                Description
                <textarea
                  rows="4"
                  value={gradeForm.description}
                  onChange={(e) =>
                    setGradeForm((p) => ({ ...p, description: e.target.value }))
                  }
                />
              </label>

              <label className="check-inline">
                <input
                  type="checkbox"
                  checked={gradeForm.active}
                  onChange={(e) =>
                    setGradeForm((p) => ({ ...p, active: e.target.checked }))
                  }
                />
                Active
              </label>

              <div className="payroll-form-actions">
                <button type="submit" className="primary-btn">
                  Save Grade
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {showApplyModal ? (
        <div className="payroll-modal-overlay">
          <div className="payroll-modal">
            <div className="payroll-modal-header">
              <h3>Apply Grade Template to Employee</h3>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setShowApplyModal(false)}
              >
                Close
              </button>
            </div>

            <form className="payroll-form" onSubmit={handleApplyGrade}>
              <div className="payroll-grid payroll-grid-2">
                <label>
                  Employee
                  <select
                    value={applyForm.employee_id}
                    onChange={(e) =>
                      setApplyForm((p) => ({
                        ...p,
                        employee_id: e.target.value,
                      }))
                    }
                    required
                  >
                    <option value="">Select employee</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.employee_code} - {emp.first_name} {emp.last_name}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Grade
                  <select
                    value={applyForm.grade_id}
                    onChange={(e) =>
                      setApplyForm((p) => ({ ...p, grade_id: e.target.value }))
                    }
                    required
                  >
                    <option value="">Select grade</option>
                    {grades.map((row) => {
                      const level = pickOne(row.payroll_job_levels);
                      return (
                        <option key={row.id} value={row.id}>
                          {level?.level_name || "Level"} - {row.grade_name}
                        </option>
                      );
                    })}
                  </select>
                </label>

                <label>
                  Apply Mode
                  <select
                    value={applyForm.mode}
                    onChange={(e) =>
                      setApplyForm((p) => ({ ...p, mode: e.target.value }))
                    }
                  >
                    <option value="replace">Replace employee setup</option>
                    <option value="fill_empty">Fill empty only</option>
                  </select>
                </label>
              </div>

              <div className="payroll-form-actions">
                <button
                  type="submit"
                  className="primary-btn"
                  disabled={applyingGrade}
                >
                  <Users size={16} />
                  {applyingGrade ? "Applying..." : "Apply Grade Template"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
